import mondaySdk from 'monday-sdk-js';
import apiClient from './client';
import { sanitize, sanitizeNullable } from '../utils/sanitize';

const monday = mondaySdk();

// ─── Types ────────────────────────────────────────────────────────────────────

export type ReportPeriod =
  | 'current_week'
  | 'previous_week'
  | 'previous_2_weeks'
  | 'previous_4_weeks'
  | 'next_2_weeks'
  | 'next_4_weeks'
  | 'current_and_next_4_weeks'
  | 'next_8_weeks'
  | 'next_12_weeks'
  | 'current_month'
  | 'previous_month'
  | 'next_month'
  | 'current_quarter'
  | 'next_quarter'
  | 'current_year'
  | 'last_year'
  | 'next_year'
  | 'last_quarter'
  | 'custom';

export type ReportScope = 'day' | 'week' | 'month';

export interface ReportConfig {
  period: ReportPeriod;
  scope?: ReportScope;
  customStartDate?: string;
  customEndDate?: string;
  userIds?: string[];
  teamIds?: string[];
  boardIds?: string[];
  groupBy: 'none' | 'board' | 'item' | 'team' | 'category';
}

export interface SavedReport {
  id: string;
  name: string;
  report_type: string;
  config: ReportConfig;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ReportBookmark {
  id: string;
  name: string;
  report_type: string;
  config: ReportConfig;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// ─── Unified column type (replaces ReportDateColumn & ReportWeekColumn) ──────

/** A single column in the report table — can represent a day, week, or month */
export interface ReportColumn {
  key: string;          // unique identifier
  label: string;        // display label ("15", "Jan 26 - Feb 1", "January 2025")
  startDate: string;    // first date in the column's range (YYYY-MM-DD)
  endDate: string;      // last date in the column's range (YYYY-MM-DD)
  isWeekend?: boolean;  // only meaningful for day scope
}

// ─── Utilization cell (scope-agnostic) ──────────────────────────────────────

/** A data cell in the utilization report — can represent a day, week, or month */
export interface UtilizationCell {
  startDate: string;
  endDate: string;
  availableHours: number;
  scheduledHours: number;
  utilizationRate: number;
  isHoliday: boolean;
  isLeave: boolean;
  isWeekend: boolean;
}

/** Per-board breakdown sub-row (shown under a user when groupBy === 'board') */
export interface BoardSubRow {
  boardId: string;
  boardName: string;
  totalScheduledHours: number;
  utilizationRate: number;
  cells: UtilizationCell[];
}

/** Per-item breakdown sub-row (shown under a user when groupBy === 'item') */
export interface ItemSubRow {
  itemId: string;
  itemName: string;
  boardId: string;
  boardName: string;
  totalScheduledHours: number;
  utilizationRate: number;
  cells: UtilizationCell[];
}

export interface UtilizationReportRow {
  userId: string;
  userName: string;
  userPhoto: string | null;
  teamName: string | null;
  totalAvailableHours: number;
  totalScheduledHours: number;
  utilizationRate: number;
  cells: UtilizationCell[];
  /** Per-board breakdown (populated when groupBy === 'board') */
  boardSubRows?: BoardSubRow[];
  /** Per-item breakdown (populated when groupBy === 'item') */
  itemSubRows?: ItemSubRow[];
}

export interface UtilizationReportGroup {
  groupId: string;
  groupName: string;
  totalAvailableHours: number;
  totalScheduledHours: number;
  utilizationRate: number;
  rows: UtilizationReportRow[];
}

export interface UtilizationReportResponse {
  reportType: 'utilization_forecast';
  generatedAt: string;
  period: { startDate: string; endDate: string };
  scope: ReportScope;
  columns: ReportColumn[];
  groups: UtilizationReportGroup[];
  summary: {
    totalAvailableHours: number;
    totalScheduledHours: number;
    overallUtilizationRate: number;
    totalUsers: number;
  };
}

// ─── Board Resources Forecast types ──────────────────────────────────────────

/** Person sub-row under a board row */
export interface BoardPersonSubRow {
  userId: string;
  userName: string;
  totalScheduledHours: number;
  cells: number[]; // hours per column
}

/** Board row (primary row in Board Resources Forecast) */
export interface BoardResourcesRow {
  boardId: string;
  boardName: string;
  totalScheduledHours: number;
  cells: number[]; // aggregated hours per column
  personSubRows: BoardPersonSubRow[];
}

export interface BoardResourcesReportResponse {
  reportType: 'board_resources_forecast';
  generatedAt: string;
  period: { startDate: string; endDate: string };
  scope: ReportScope;
  columns: ReportColumn[];
  rows: BoardResourcesRow[];
  summary: {
    totalScheduledHours: number;
    totalBoards: number;
  };
}

// ─── Leave Report types ─────────────────────────────────────────────────────

export interface LeaveTypeInfo {
  id: string;
  name: string;
  abbreviation: string;
  color: string;
}

export interface LeaveTypeDaySummary {
  leaveTypeId: string;
  days: number;
}

export interface LeaveReportCellEntry {
  leaveTypeId: string;
  leaveTypeName: string;
  abbreviation: string;
  color: string;
  days: number;
  hours: number;
}

export interface LeaveReportCell {
  startDate: string;
  endDate: string;
  totalHours: number;
  holidayDays: number;
  entries: LeaveReportCellEntry[];
}

export interface LeaveReportRow {
  userId: string;
  userName: string;
  userPhoto: string | null;
  totalDays: number;
  holidayDays: number;
  leaveTypeDays: LeaveTypeDaySummary[];
  cells: LeaveReportCell[];
}

export interface LeaveReportGroup {
  groupId: string;
  groupName: string;
  rows: LeaveReportRow[];
}

export interface LeaveReportSummary {
  totalUsers: number;
  totalLeaveDays: number;
  averageLeaveDaysPerUser: number;
}

export interface LeaveReportResponse {
  reportType: 'leave_report';
  generatedAt: string;
  period: { startDate: string; endDate: string };
  columns: ReportColumn[];
  groups: LeaveReportGroup[];
  summary: LeaveReportSummary;
  leaveTypes: LeaveTypeInfo[];
}

// ─── Detailed Worklog Report types ──────────────────────────────────────────

export interface WorklogReportRow {
  worklogId: string;
  userId: string;
  userName: string;
  boardId: string;
  boardName: string;
  itemId: string;
  itemName: string;
  date: string;
  hours: number;
  description: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categoryIsBillable: boolean | null;
  approvalStatus: string | null;
  approverUserId: string | null;
  approverName: string | null;
}

export interface WorklogReportGroup {
  groupId: string;
  groupName: string;
  rows: WorklogReportRow[];
}

export interface WorklogReportSummary {
  totalWorklogs: number;
  totalHours: number;
  totalBillableHours: number;
  totalNonBillableHours: number;
  totalUsers: number;
}

export interface WorklogReportResponse {
  reportType: 'detailed_worklog';
  generatedAt: string;
  period: { startDate: string; endDate: string };
  groups: WorklogReportGroup[];
  summary: WorklogReportSummary;
}

// ─── Planned vs Actual Report types ──────────────────────────────────────────

export interface WorklogSummaryItem {
  mondayItemId: string;
  mondayBoardId: string;
  totalHours: number;
}

export interface PlannedVsActualItemRow {
  itemId: string;
  boardId: string;
  boardName: string;
  itemName: string;
  assignees: string;
  startDate: string | null;
  endDate: string | null;
  originalEstimate: number;
  timeSpent: number;
  remainingEstimate: number;
  deviation: number;
  deviationPercent: number;
  isSubitem: boolean;
}

export interface PlannedVsActualBoardGroup {
  boardId: string;
  boardName: string;
  items: PlannedVsActualItemRow[];
  summary: {
    totalItems: number;
    totalAssignees: number;
    originalEstimate: number;
    timeSpent: number;
    remainingEstimate: number;
    deviation: number;
    deviationPercent: number;
  };
}

export interface PlannedVsActualBoardRow {
  boardId: string;
  boardName: string;
  totalItems: number;
  totalAssignees: number;
  originalEstimate: number;
  timeSpent: number;
  remainingEstimate: number;
  deviation: number;
  deviationPercent: number;
}

export interface PlannedVsActualReportResponse {
  reportType: 'planned_vs_actual';
  generatedAt: string;
  period: { startDate: string; endDate: string };
  boardGroups: PlannedVsActualBoardGroup[];
  boardRows: PlannedVsActualBoardRow[];
  grandTotal: {
    totalItems: number;
    totalAssignees: number;
    originalEstimate: number;
    timeSpent: number;
    remainingEstimate: number;
    deviation: number;
    deviationPercent: number;
  };
}

/** Union of all report data types */
export type ReportData =
  | UtilizationReportResponse
  | BoardResourcesReportResponse
  | LeaveReportResponse
  | WorklogReportResponse
  | PlannedVsActualReportResponse;

// ─── Sanitization helpers ─────────────────────────────────────────────────────

function sanitizeUtilizationReport(
  report: UtilizationReportResponse,
): UtilizationReportResponse {
  return {
    ...report,
    groups: report.groups.map((group) => ({
      ...group,
      groupName: sanitize(group.groupName),
      rows: group.rows.map((row) => ({
        ...row,
        userName: sanitize(row.userName),
        teamName: sanitizeNullable(row.teamName),
        boardSubRows: row.boardSubRows?.map((sr) => ({
          ...sr,
          boardName: sanitize(sr.boardName),
        })),
        itemSubRows: row.itemSubRows?.map((sr) => ({
          ...sr,
          itemName: sanitize(sr.itemName),
          boardName: sanitize(sr.boardName),
        })),
      })),
    })),
  };
}

function sanitizeBoardResourcesReport(
  report: BoardResourcesReportResponse,
): BoardResourcesReportResponse {
  return {
    ...report,
    rows: report.rows.map((row) => ({
      ...row,
      boardName: sanitize(row.boardName),
      personSubRows: row.personSubRows.map((sr) => ({
        ...sr,
        userName: sanitize(sr.userName),
      })),
    })),
  };
}

function sanitizeLeaveReport(report: LeaveReportResponse): LeaveReportResponse {
  return {
    ...report,
    groups: report.groups.map((group) => ({
      ...group,
      groupName: sanitize(group.groupName),
      rows: group.rows.map((row) => ({
        ...row,
        userName: sanitize(row.userName),
      })),
    })),
  };
}

function sanitizeWorklogReport(
  report: WorklogReportResponse,
): WorklogReportResponse {
  return {
    ...report,
    groups: report.groups.map((group) => ({
      ...group,
      groupName: sanitize(group.groupName),
      rows: group.rows.map((row) => ({
        ...row,
        userName: sanitize(row.userName),
        boardName: sanitize(row.boardName),
        itemName: sanitize(row.itemName),
        description: sanitizeNullable(row.description),
        categoryName: sanitizeNullable(row.categoryName),
        approverName: sanitizeNullable(row.approverName),
      })),
    })),
  };
}

// ─── API Functions ────────────────────────────────────────────────────────────

export async function getSavedReports(): Promise<SavedReport[]> {
  const { data } = await apiClient.get<SavedReport[]>('/reports');
  return data;
}

export async function createSavedReport(payload: {
  name: string;
  reportType: string;
  config: ReportConfig;
}): Promise<SavedReport> {
  const { data } = await apiClient.post<SavedReport>('/reports', payload);
  return data;
}

export async function updateSavedReport(
  id: string,
  payload: { name?: string; config?: ReportConfig },
): Promise<SavedReport> {
  const { data } = await apiClient.patch<SavedReport>(
    `/reports/${id}`,
    payload,
  );
  return data;
}

export async function deleteSavedReport(id: string): Promise<void> {
  await apiClient.delete(`/reports/${id}`);
}

/**
 * Fetch daily availability (capacity − holidays − leaves) from the backend.
 * This uses only plugin DB data — no monday.com API calls on the server.
 * The frontend then merges this with monday.com scheduled items fetched via SDK.
 */
export async function fetchAvailability(
  payload: {
    userIds: string[];
    startDate: string;
    endDate: string;
  },
  signal?: AbortSignal,
): Promise<import('../utils/report-calculation').AvailabilityResponse> {
  const { data } = await apiClient.post<
    import('../utils/report-calculation').AvailabilityResponse
  >('/reports/availability', payload, { signal });
  return data;
}

/**
 * Fetch leave report data from the backend.
 * Entirely backend-driven — no monday.com API calls needed.
 */
export async function fetchLeaveReport(payload: {
  startDate: string;
  endDate: string;
  userIds?: string[];
  teamIds?: string[];
  scope?: ReportScope;
  firstDayOfWeek?: 0 | 1;
}): Promise<LeaveReportResponse> {
  const { data } = await apiClient.post<LeaveReportResponse>(
    '/reports/leave',
    payload,
  );
  return sanitizeLeaveReport(data);
}

/**
 * Fetch detailed worklog report from the backend, then resolve
 * board and item names from the monday.com API.
 */
export async function fetchWorklogReport(payload: {
  startDate: string;
  endDate: string;
  userIds?: string[];
  teamIds?: string[];
}): Promise<WorklogReportResponse> {
  const { data } = await apiClient.post<WorklogReportResponse>(
    '/reports/worklogs',
    payload,
  );
  const report = sanitizeWorklogReport(data);

  // Collect unique board and item IDs that need name resolution
  const boardIds = new Set<string>();
  const itemIds = new Set<string>();
  for (const group of report.groups) {
    for (const row of group.rows) {
      if (row.boardId) boardIds.add(row.boardId);
      if (row.itemId) itemIds.add(row.itemId);
    }
  }

  // Resolve board names
  const boardNameMap = new Map<string, string>();
  if (boardIds.size > 0) {
    try {
      const resp = await monday.api(
        `query ($ids: [ID!]!) { boards(ids: $ids) { id name } }`,
        { variables: { ids: Array.from(boardIds) } },
      );
      const boards = (resp.data as { boards?: Array<{ id: string; name: string }> })?.boards;
      if (boards) {
        for (const b of boards) boardNameMap.set(b.id, b.name);
      }
    } catch { /* keep IDs as fallback */ }
  }

  // Resolve item names (batch in chunks of 100 to avoid API limits)
  const itemNameMap = new Map<string, string>();
  if (itemIds.size > 0) {
    const idArray = Array.from(itemIds);
    const chunks: string[][] = [];
    for (let i = 0; i < idArray.length; i += 100) {
      chunks.push(idArray.slice(i, i + 100));
    }
    for (const chunk of chunks) {
      try {
        const resp = await monday.api(
          `query ($ids: [ID!]!) { items(ids: $ids) { id name } }`,
          { variables: { ids: chunk } },
        );
        const items = (resp.data as { items?: Array<{ id: string; name: string }> })?.items;
        if (items) {
          for (const item of items) itemNameMap.set(item.id, item.name);
        }
      } catch { /* keep IDs as fallback */ }
    }
  }

  // Replace IDs with resolved names
  for (const group of report.groups) {
    for (const row of group.rows) {
      const boardName = boardNameMap.get(row.boardId);
      if (boardName) row.boardName = boardName;
      const itemName = itemNameMap.get(row.itemId);
      if (itemName) row.itemName = itemName;
    }
  }

  return report;
}

/**
 * Fetch aggregated worklog hours per item across boards for a date range.
 * Used by the Planned vs Actual report.
 */
export async function getWorklogSummary(
  boardIds: string[],
  startDate: string,
  endDate: string,
  userIds?: string[],
): Promise<WorklogSummaryItem[]> {
  const params: Record<string, string> = {
    boardIds: boardIds.join(','),
    startDate,
    endDate,
  };
  if (userIds && userIds.length > 0) {
    params.userIds = userIds.join(',');
  }
  const { data } = await apiClient.get<WorklogSummaryItem[]>(
    '/worklogs/summary-by-item',
    { params },
  );
  return data;
}

// ─── Bookmark API Functions ──────────────────────────────────────────────────

export async function getBookmarks(): Promise<ReportBookmark[]> {
  const { data } = await apiClient.get<ReportBookmark[]>('/reports/bookmarks');
  return data;
}

export async function createBookmark(payload: {
  name: string;
  reportType: string;
  config: ReportConfig;
}): Promise<ReportBookmark> {
  const { data } = await apiClient.post<ReportBookmark>(
    '/reports/bookmarks',
    payload,
  );
  return data;
}

export async function updateBookmark(
  id: string,
  payload: { name?: string },
): Promise<ReportBookmark> {
  const { data } = await apiClient.patch<ReportBookmark>(
    `/reports/bookmarks/${id}`,
    payload,
  );
  return data;
}

export async function deleteBookmark(id: string): Promise<void> {
  await apiClient.delete(`/reports/bookmarks/${id}`);
}
